import { type ComponentPropsWithoutRef, type ComponentRef, forwardRef } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { RocketIcon, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@r/utils/utils.ts';
import { FormattedNumber } from '@r/components/formatted-number.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import type { LogicTypeTargetStatusCompanyReadModel } from '@r/api/generated/client.schemas.ts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@ui/tooltip.tsx';

type TargetStatus = LogicTypeTargetStatusCompanyReadModel;

interface ProgressTargetProps extends ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  targetStatus?: TargetStatus;
  currencySymbol?: string;
  isTargetPointLogic?: boolean;
}

const ProgressTarget = forwardRef<ComponentRef<typeof ProgressPrimitive.Root>, ProgressTargetProps>(
  ({ className, targetStatus, currencySymbol, isTargetPointLogic = false, ...props }, ref) => {
    const { t } = useTranslation();

    const currentProgressValue = targetStatus?.value ?? 0;
    const targetValue = targetStatus?.targetValue ?? 0;
    const progressPercentage = targetValue > 0 ? (currentProgressValue / targetValue) * 100 : 0;
    const radixValue = Math.max(0, Math.min(100, progressPercentage));
    const labelPositionPercentage = Math.max(0, Math.min(100, progressPercentage));
    const amountLeftforTarget = Math.max(0, targetValue - currentProgressValue);
    const valueSuffix = isTargetPointLogic
      ? ` ${pointsTranslation(t, currentProgressValue, false)}`
      : ` ${currencySymbol ?? ''}`;
    const shouldDisplayTooltip = currentProgressValue > 1;
    const progressBar = (
      <ProgressPrimitive.Root
        ref={ref}
        className={cn('overflow-hidden relative w-full h-3 rounded-full bg-secondary', className)}
        value={radixValue}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className="relative flex-1 w-full h-full rounded-full transition-transform duration-500 ease-out bg-primary"
          style={{
            transform: `translateX(-${100 - (radixValue || 0)}%)`,
            animation: 'full-width-basic 2s ease-out forwards'
          }}
        />
      </ProgressPrimitive.Root>
    );

    return (
      <div className="px-5 pt-[3.6rem] pb-[2.5rem] sm:px-16">
        <div className="relative">
          <div
            className="absolute top-[-4rem] flex text-primary-foreground flex-nowrap items-center whitespace-nowrap rounded-md bg-primary px-3 py-2 text-sm xl:flex z-10"
            style={{
              left: `${labelPositionPercentage}%`,
              transform: `translateX(-${labelPositionPercentage}%)`
            }}
          >
            <div className="flex justify-center items-center w-6 rounded-full border aspect-square border-primary-foreground">
              <RocketIcon size={13} />
            </div>
            <span className="hidden sm:ml-2 sm:block">
              {t('logic.target.cursor.label', {
                amount: String(FormattedNumber({ value: amountLeftforTarget, style: 'decimal' })),
                currencySymbol: isTargetPointLogic
                  ? ` ${pointsTranslation(t, amountLeftforTarget, false)}`
                  : currencySymbol
              })}
            </span>
            <div className="absolute bottom-[-1.2rem] left-0 w-full justify-center flex">
              <ChevronDown className="stroke-primary" size={20} />
            </div>
          </div>

          {shouldDisplayTooltip ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>{progressBar}</div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="flex gap-2 justify-center items-center whitespace-nowrap">
                    <span className="w-3 h-3 rounded-sm bg-primary" />
                    <span>
                      <span className="font-bold">
                        <FormattedNumber
                          value={currentProgressValue}
                          style="decimal"
                          suffix={valueSuffix}
                        />
                      </span>{' '}
                      {t('logic.progress.tooltip.generated.company')}
                    </span>
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            progressBar
          )}

          <div className="flex justify-between mt-2 text-sm font-medium text-foreground">
            <FormattedNumber
              value={0}
              style={isTargetPointLogic ? 'decimal' : undefined}
              suffix={isTargetPointLogic ? ` ${pointsTranslation(t, 0, false)}` : undefined}
            />
            <span>
              <FormattedNumber
                value={targetValue}
                style={isTargetPointLogic ? 'decimal' : undefined}
                suffix={
                  isTargetPointLogic ? ` ${pointsTranslation(t, targetValue, false)}` : undefined
                }
              />
            </span>
          </div>
        </div>
      </div>
    );
  }
);

ProgressTarget.displayName = ProgressPrimitive.Root.displayName;

export { ProgressTarget };
